/**
 * Submission-quality signals for lead analytics.
 *
 * Every successful form submission emits a correlation id plus a compact
 * summary of WHICH attribution signals were actually present, so drop-offs
 * and untracked traffic are visible in GA4 without exporting raw leads:
 *
 *   correlation_id    — generated client-side, also stored on the lead row
 *   lead_id           — backend row id (emitted once the insert resolves)
 *   has_utm           — any last-touch utm_* present
 *   has_first_touch   — any ft_utm_* present
 *   has_click_id      — gclid / fbclid / msclkid / ttclid present
 *   has_placement     — the visitor arrived via a tracked CTA placement
 */
import { getLastPlacement } from "@/lib/cta-placement";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const CLICK_IDS = ["gclid", "fbclid", "msclkid", "ttclid"] as const;

/** Short, sortable, collision-safe enough for correlating a single submit. */
export function newCorrelationId(prefix = "lead"): string {
  const rand =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 12)
      : Math.random().toString(36).slice(2, 14);
  return `${prefix}_${Date.now().toString(36)}_${rand}`;
}

export type AttributionCoverage = {
  has_utm: boolean;
  utm_fields_present: number;
  utm_fields: string;
  has_first_touch_utm: boolean;
  has_click_id: boolean;
  click_id_types: string;
  has_referrer: boolean;
  has_placement: boolean;
  placement: string;
  placement_cta_id: string;
};

const filled = (p: Record<string, unknown>, key: string) =>
  typeof p[key] === "string" && (p[key] as string).trim().length > 0;

/** Summarize which attribution fields a lead payload actually carried. */
export function attributionCoverage(payload: Record<string, unknown>): AttributionCoverage {
  const utm = UTM_KEYS.filter((k) => filled(payload, k));
  const ft = UTM_KEYS.filter((k) => filled(payload, `ft_${k}`));
  const clicks = CLICK_IDS.filter((k) => filled(payload, k));
  const placement = getLastPlacement();

  return {
    has_utm: utm.length > 0,
    utm_fields_present: utm.length,
    utm_fields: utm.join(","),
    has_first_touch_utm: ft.length > 0,
    has_click_id: clicks.length > 0,
    click_id_types: clicks.join(","),
    has_referrer: filled(payload, "referrer"),
    has_placement: Boolean(placement?.placement),
    placement: placement?.placement ?? "none",
    placement_cta_id: placement?.ctaId ?? "none",
  };
}
